import React from "react";
import { Paper, Stack } from "@mui/material";
import styled, { useTheme } from "styled-components";
import AnalysisSlider from "src/scenes/causal-analysis/components/analysis-slider";
import Xarrow, { Xwrapper } from "react-xarrows";
import addEdgeRowPropertiesToNodes from "src/scenes/causal-analysis/components/add-edges-rows-to-nodes";
import causalValueFormatter from "src/scenes/causal-analysis/components/causal-value-formatter";
import namesAndHelpersObject from "src/scenes/causal-analysis/components/names-and-helpers-object";
import showMoreArrow from "src/scenes/causal-analysis/components/show-more-arrow";

const CustomPaper = styled(Paper)`
    border-radius: 14px;
    height: 159px;
    width: 294px;
    cursor: pointer;
    transition: opacity 0.4s ease-out;
    position: static;
    // z-index: 0;
    &:hover {
        transform: scale(1.03);
        border: 1px solid ${({ theme }) => theme.palette.primary.main};
    }
    &.fadeOut {
        opacity: 0;
    }
`;

const PaperTitle = styled.p`
    margin: 0;
    padding: 7px 0 6px 18px;
    font-size: 11px;
    background-color: #5b5b5b;
    border-radius: 14px 14px 0 0;
    backface-visibility: hidden;
    transform: translateZ(0) scale(1, 1);
`;

const PaperHelper = styled.p`
    font-size: 11px;
    font-weight: 200;
    padding: 0 18px 0 18px;
    margin-bottom: 0;
    height: 48px;
    backface-visibility: hidden;
    transform: translateZ(0) scale(1, 1);
`;

const Text = styled.p`
    margin: 0;
    padding-right: 3px;
`;

const CalculatedText = styled.p`
    margin: 0;
    padding-right: 3px;
    color: ${({ theme, initial, calculated }) => {
        if (initial === calculated) return theme.palette.secondary.main;
        if (initial > calculated) return theme.palette.missions.high;
        if (initial < calculated) return theme.palette.primary.main;
        return null;
    }};
`;

const CalculateLineColor = ({ weight, theme }) => {
    if (weight > 0) return theme.palette.primary.main;
    if (weight < 0) return theme.palette.missions.high;
    if (weight === 0) return theme.palette.secondary.main;
    return null;
};

const CustomLabel = ({ text }) => {
    return (
        <span
            style={{
                fontSize: "12px",
                backgroundColor: "black",
                borderRadius: "4px",
                padding: "6px",
            }}
        >
            {Math.abs(parseFloat(text)) < 0.1
                ? parseFloat(text).toFixed(2)
                : parseFloat(text).toFixed(1)}
        </span>
    );
};

const calculateStrokeWidth = (weight) => {
    const absoluteWeight = Math.abs(weight).toFixed(1);
    let formattedWeight;
    if (absoluteWeight < 1) {
        formattedWeight = 1;
        return formattedWeight;
    }
    return absoluteWeight;
};
const AnalysisTreeProduction = ({
    data,
    setIsDirty,
    isCalculated,
    interventionData,
    setInterventionData,
    interventionResponse,
}) => {
    if (!data || data.length === 0) return null;
    const theme = useTheme();
    const [hoveredId, setHoveredId] = React.useState(null);
    const [rowControls, setRowControls] = React.useState({
        row1: true,
        row2: true,
        row3: true,
        row4: false,
        row5: false,
        row6: false,
    });

    const BeefedUpNodes = addEdgeRowPropertiesToNodes(data);

    return (
        <Stack direction="column" mb={2} style={{ width: "100%" }} alignContent="flex-start">
            <Xwrapper>
                <Stack direction="row" spacing={2} mt={7} id="row-1">
                    {BeefedUpNodes.nodes
                        .filter((item) => item.row === "row1")
                        .map((item) => (
                            <CustomPaper
                                id={item.name}
                                key={item.name}
                                onClick={() =>
                                    setRowControls((prevControls) => ({
                                        ...prevControls,
                                        row2: !prevControls.row2,
                                        row3: prevControls.row3
                                            ? !prevControls.row3
                                            : prevControls.row3,
                                        row4: prevControls.row4
                                            ? !prevControls.row4
                                            : prevControls.row4,
                                        row5: prevControls.row5
                                            ? !prevControls.row5
                                            : prevControls.row5,
                                        row6: prevControls.row6
                                            ? !prevControls.row6
                                            : prevControls.row6,
                                    }))
                                }
                                className={rowControls[item.row] ? "" : "fadeOut"}
                            >
                                <PaperTitle>{namesAndHelpersObject[item.name].name}</PaperTitle>
                                <Stack
                                    direction="column"
                                    justifyContent="space-between"
                                    style={{ height: "72%" }}
                                >
                                    <PaperHelper>
                                        {namesAndHelpersObject[item.name].helper}
                                    </PaperHelper>
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        style={{ width: "100%", paddingRight: "16px" }}
                                    >
                                        <Stack direction="row" style={{ marginTop: "8px" }}>
                                            <Text>
                                                {causalValueFormatter({
                                                    units: item.units,
                                                    value: item.mean,
                                                })}
                                            </Text>
                                            {isCalculated &&
                                                interventionResponse &&
                                                interventionResponse.intervention && (
                                                    <CalculatedText
                                                        initial={
                                                            interventionResponse.baseline[item.name]
                                                        }
                                                        calculated={
                                                            interventionResponse.intervention[
                                                                item.name
                                                            ]
                                                        }
                                                    >
                                                        {`(${causalValueFormatter({
                                                            units: item.units,
                                                            value: interventionResponse
                                                                .intervention[item.name],
                                                        })})`}
                                                    </CalculatedText>
                                                )}
                                            <Text style={{ fontWeight: "200" }}>{item.type}</Text>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </CustomPaper>
                        ))}
                </Stack>
                {showMoreArrow({ rowControls, setRowControls, rowNumber: 1 })}
                <Stack direction="row" spacing={2} mt={7} id="row-2">
                    {rowControls.row2 &&
                        BeefedUpNodes.nodes
                            .filter((item) => item.row === "row2")
                            .map((item) => (
                                <CustomPaper
                                    id={item.name}
                                    key={item.name}
                                    onClick={() =>
                                        setRowControls((prevControls) => ({
                                            ...prevControls,
                                            row3: !prevControls.row3,
                                            row4: prevControls.row4
                                                ? !prevControls.row4
                                                : prevControls.row4,
                                            row5: prevControls.row5
                                                ? !prevControls.row5
                                                : prevControls.row5,
                                            row6: prevControls.row6
                                                ? !prevControls.row6
                                                : prevControls.row6,
                                        }))
                                    }
                                    onMouseEnter={() => setHoveredId(item.name)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    className={rowControls[item.row] ? "" : "fadeOut"}
                                >
                                    <PaperTitle>{namesAndHelpersObject[item.name].name}</PaperTitle>
                                    <Stack
                                        direction="column"
                                        justifyContent="space-between"
                                        style={{ height: "79%" }}
                                    >
                                        <PaperHelper>
                                            {namesAndHelpersObject[item.name].helper}
                                        </PaperHelper>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            style={{ width: "100%", paddingRight: "16px" }}
                                        >
                                            <AnalysisSlider
                                                defaultValue={item.mean}
                                                mean={item.mean}
                                                std={item.std}
                                                max={Math.min(item.mean + 2 * item.std, item.upper)}
                                                min={Math.max(item.mean - 2 * item.std, item.lower)}
                                                units={item.units}
                                                setIsDirty={setIsDirty}
                                                interventionData={interventionData}
                                                setInterventionData={setInterventionData}
                                                id={item.name}
                                            />
                                            <Stack direction="row">
                                                <Text>
                                                    {causalValueFormatter({
                                                        units: item.units,
                                                        value: item.mean,
                                                    })}
                                                </Text>
                                                {isCalculated &&
                                                    interventionResponse &&
                                                    interventionResponse.intervention && (
                                                        <CalculatedText
                                                            initial={
                                                                interventionResponse.baseline[
                                                                    item.name
                                                                ]
                                                            }
                                                            calculated={
                                                                interventionResponse.intervention[
                                                                    item.name
                                                                ]
                                                            }
                                                        >
                                                            {`(${causalValueFormatter({
                                                                units: item.units,
                                                                value: interventionResponse
                                                                    .intervention[item.name],
                                                            })})`}
                                                        </CalculatedText>
                                                    )}
                                                <Text style={{ fontWeight: "200" }}>
                                                    {item.type}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                    {item.edges.map((edge) => (
                                        <Xarrow
                                            zIndex={-100}
                                            key={`${edge.source}-${edge.target}`}
                                            start={edge.source}
                                            end={edge.target}
                                            startAnchor="top"
                                            endAnchor="bottom"
                                            headSize={0}
                                            lineColor={
                                                hoveredId === null || hoveredId === item.name
                                                    ? CalculateLineColor({
                                                          weight: edge.weight,
                                                          theme,
                                                      })
                                                    : "#3d3d3d"
                                            }
                                            strokeWidth={calculateStrokeWidth(edge.weight)}
                                            animateDrawing
                                            curveness={0.3}
                                            labels={
                                                hoveredId === item.name ? (
                                                    <CustomLabel text={`${edge.weight}`} />
                                                ) : null
                                            }
                                            dashness={{
                                                strokeLen: 10,
                                                nonStrokeLen: 10,
                                                animation: -0.5,
                                            }}
                                        />
                                    ))}
                                </CustomPaper>
                            ))}
                </Stack>
                {showMoreArrow({ rowControls, setRowControls, rowNumber: 2 })}
                <Stack direction="row" spacing={2} mt={7} id="row-3">
                    {rowControls.row3 &&
                        BeefedUpNodes.nodes
                            .filter((item) => item.row === "row3")
                            .map((item) => (
                                <CustomPaper
                                    id={item.name}
                                    key={item.name}
                                    onClick={() =>
                                        setRowControls((prevControls) => ({
                                            ...prevControls,
                                            row4: !prevControls.row4,
                                            row5: prevControls.row5
                                                ? !prevControls.row5
                                                : prevControls.row5,
                                            row6: prevControls.row6
                                                ? !prevControls.row6
                                                : prevControls.row6,
                                        }))
                                    }
                                    onMouseEnter={() => setHoveredId(item.name)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    className={rowControls[item.row] ? "" : "fadeOut"}
                                >
                                    <PaperTitle>{namesAndHelpersObject[item.name].name}</PaperTitle>
                                    <Stack
                                        direction="column"
                                        justifyContent="space-between"
                                        style={{ height: "79%" }}
                                    >
                                        <PaperHelper>
                                            {namesAndHelpersObject[item.name].helper}
                                        </PaperHelper>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            style={{ width: "100%", paddingRight: "16px" }}
                                        >
                                            <AnalysisSlider
                                                defaultValue={item.mean}
                                                mean={item.mean}
                                                std={item.std}
                                                max={Math.min(item.mean + 2 * item.std, item.upper)}
                                                min={Math.max(item.mean - 2 * item.std, item.lower)}
                                                units={item.units}
                                                setIsDirty={setIsDirty}
                                                interventionData={interventionData}
                                                setInterventionData={setInterventionData}
                                                id={item.name}
                                            />
                                            <Stack direction="row">
                                                <Text>
                                                    {causalValueFormatter({
                                                        units: item.units,
                                                        value: item.mean,
                                                    })}
                                                </Text>
                                                {isCalculated &&
                                                    interventionResponse &&
                                                    interventionResponse.intervention && (
                                                        <CalculatedText
                                                            initial={
                                                                interventionResponse.baseline[
                                                                    item.name
                                                                ]
                                                            }
                                                            calculated={
                                                                interventionResponse.intervention[
                                                                    item.name
                                                                ]
                                                            }
                                                        >
                                                            {`(${causalValueFormatter({
                                                                units: item.units,
                                                                value: interventionResponse
                                                                    .intervention[item.name],
                                                            })})`}
                                                        </CalculatedText>
                                                    )}
                                                <Text style={{ fontWeight: "200" }}>
                                                    {item.type}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                    {item.edges.map((edge) => (
                                        <Xarrow
                                            zIndex={-100}
                                            key={`${edge.source}-${edge.target}`}
                                            start={edge.source}
                                            end={edge.target}
                                            startAnchor="top"
                                            endAnchor="bottom"
                                            headSize={0}
                                            lineColor={
                                                hoveredId === null || hoveredId === item.name
                                                    ? CalculateLineColor({
                                                          weight: edge.weight,
                                                          theme,
                                                      })
                                                    : "#3d3d3d"
                                            }
                                            strokeWidth={calculateStrokeWidth(edge.weight)}
                                            animateDrawing
                                            curveness={0.3}
                                            labels={
                                                hoveredId === item.name ? (
                                                    <CustomLabel text={`${edge.weight}`} />
                                                ) : null
                                            }
                                            dashness={{
                                                strokeLen: 10,
                                                nonStrokeLen: 10,
                                                animation: -0.5,
                                            }}
                                        />
                                    ))}
                                </CustomPaper>
                            ))}
                </Stack>
                {showMoreArrow({ rowControls, setRowControls, rowNumber: 3 })}
                <Stack direction="row" spacing={2} mt={7} id="row-4">
                    {rowControls.row4 &&
                        BeefedUpNodes.nodes
                            .filter((item) => item.row === "row4")
                            .map((item) => (
                                <CustomPaper
                                    id={item.name}
                                    key={item.name}
                                    onClick={() =>
                                        setRowControls((prevControls) => ({
                                            ...prevControls,
                                            row5: !prevControls.row5,
                                            row6: prevControls.row6
                                                ? !prevControls.row6
                                                : prevControls.row6,
                                        }))
                                    }
                                    onMouseEnter={() => setHoveredId(item.name)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    className={rowControls[item.row] ? "" : "fadeOut"}
                                >
                                    <PaperTitle>{namesAndHelpersObject[item.name].name}</PaperTitle>
                                    <Stack
                                        direction="column"
                                        justifyContent="space-between"
                                        style={{ height: "79%" }}
                                    >
                                        <PaperHelper>
                                            {namesAndHelpersObject[item.name].helper}
                                        </PaperHelper>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            style={{ width: "100%", paddingRight: "16px" }}
                                        >
                                            <AnalysisSlider
                                                defaultValue={item.mean}
                                                mean={item.mean}
                                                std={item.std}
                                                max={Math.min(item.mean + 2 * item.std, item.upper)}
                                                min={Math.max(item.mean - 2 * item.std, item.lower)}
                                                units={item.units}
                                                setIsDirty={setIsDirty}
                                                interventionData={interventionData}
                                                setInterventionData={setInterventionData}
                                                id={item.name}
                                            />
                                            <Stack direction="row">
                                                <Text>
                                                    {causalValueFormatter({
                                                        units: item.units,
                                                        value: item.mean,
                                                    })}
                                                </Text>
                                                {isCalculated &&
                                                    interventionResponse &&
                                                    interventionResponse.intervention && (
                                                        <CalculatedText
                                                            initial={
                                                                interventionResponse.baseline[
                                                                    item.name
                                                                ]
                                                            }
                                                            calculated={
                                                                interventionResponse.intervention[
                                                                    item.name
                                                                ]
                                                            }
                                                        >
                                                            {`(${causalValueFormatter({
                                                                units: item.units,
                                                                value: interventionResponse
                                                                    .intervention[item.name],
                                                            })})`}
                                                        </CalculatedText>
                                                    )}
                                                <Text style={{ fontWeight: "200" }}>
                                                    {item.type}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                    {item.edges.map((edge) => (
                                        <Xarrow
                                            zIndex={-100}
                                            key={`${edge.source}-${edge.target}`}
                                            start={edge.source}
                                            end={edge.target}
                                            startAnchor="top"
                                            endAnchor="bottom"
                                            headSize={0}
                                            lineColor={
                                                hoveredId === null || hoveredId === item.name
                                                    ? CalculateLineColor({
                                                          weight: edge.weight,
                                                          theme,
                                                      })
                                                    : "#3d3d3d"
                                            }
                                            strokeWidth={calculateStrokeWidth(edge.weight)}
                                            animateDrawing
                                            curveness={0.3}
                                            labels={
                                                hoveredId === item.name ? (
                                                    <CustomLabel text={`${edge.weight}`} />
                                                ) : null
                                            }
                                            dashness={{
                                                strokeLen: 10,
                                                nonStrokeLen: 10,
                                                animation: -0.5,
                                            }}
                                        />
                                    ))}
                                </CustomPaper>
                            ))}
                </Stack>
                {showMoreArrow({ rowControls, setRowControls, rowNumber: 4 })}
                <Stack direction="row" spacing={2} mt={7} id="row-5">
                    {rowControls.row5 &&
                        BeefedUpNodes.nodes
                            .filter((item) => item.row === "row5")
                            .map((item) => (
                                <CustomPaper
                                    id={item.name}
                                    key={item.name}
                                    onClick={() =>
                                        setRowControls((prevControls) => ({
                                            ...prevControls,
                                            row6: !prevControls.row6,
                                        }))
                                    }
                                    onMouseEnter={() => setHoveredId(item.name)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    className={rowControls[item.row] ? "" : "fadeOut"}
                                >
                                    <PaperTitle>{namesAndHelpersObject[item.name].name}</PaperTitle>
                                    <Stack
                                        direction="column"
                                        justifyContent="space-between"
                                        style={{ height: "79%" }}
                                    >
                                        <PaperHelper>
                                            {namesAndHelpersObject[item.name].helper}
                                        </PaperHelper>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            style={{ width: "100%", paddingRight: "16px" }}
                                        >
                                            <AnalysisSlider
                                                defaultValue={item.mean}
                                                mean={item.mean}
                                                std={item.std}
                                                max={Math.min(item.mean + 2 * item.std, item.upper)}
                                                min={Math.max(item.mean - 2 * item.std, item.lower)}
                                                units={item.units}
                                                setIsDirty={setIsDirty}
                                                interventionData={interventionData}
                                                setInterventionData={setInterventionData}
                                                id={item.name}
                                            />
                                            <Stack direction="row">
                                                <Text>
                                                    {causalValueFormatter({
                                                        units: item.units,
                                                        value: item.mean,
                                                    })}
                                                </Text>
                                                {isCalculated &&
                                                    interventionResponse &&
                                                    interventionResponse.intervention && (
                                                        <CalculatedText
                                                            initial={
                                                                interventionResponse.baseline[
                                                                    item.name
                                                                ]
                                                            }
                                                            calculated={
                                                                interventionResponse.intervention[
                                                                    item.name
                                                                ]
                                                            }
                                                        >
                                                            {`(${causalValueFormatter({
                                                                units: item.units,
                                                                value: interventionResponse
                                                                    .intervention[item.name],
                                                            })})`}
                                                        </CalculatedText>
                                                    )}
                                                <Text style={{ fontWeight: "200" }}>
                                                    {item.type}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                    {item.edges.map((edge) => (
                                        <Xarrow
                                            zIndex={-100}
                                            key={`${edge.source}-${edge.target}`}
                                            start={edge.source}
                                            end={edge.target}
                                            startAnchor="top"
                                            endAnchor="bottom"
                                            headSize={0}
                                            lineColor={
                                                hoveredId === null || hoveredId === item.name
                                                    ? CalculateLineColor({
                                                          weight: edge.weight,
                                                          theme,
                                                      })
                                                    : "#3d3d3d"
                                            }
                                            strokeWidth={calculateStrokeWidth(edge.weight)}
                                            animateDrawing
                                            curveness={0.3}
                                            labels={
                                                hoveredId === item.name ? (
                                                    <CustomLabel text={`${edge.weight}`} />
                                                ) : null
                                            }
                                            dashness={{
                                                strokeLen: 10,
                                                nonStrokeLen: 10,
                                                animation: -0.5,
                                            }}
                                        />
                                    ))}
                                </CustomPaper>
                            ))}
                </Stack>
                {showMoreArrow({ rowControls, setRowControls, rowNumber: 5 })}
                <Stack direction="row" spacing={2} mt={7} id="row-6">
                    {rowControls.row6 &&
                        BeefedUpNodes.nodes
                            .filter((item) => item.row === "row6")
                            .map((item) => (
                                <CustomPaper
                                    id={item.name}
                                    key={item.name}
                                    onClick={() =>
                                        setRowControls((prevControls) => ({
                                            ...prevControls,
                                        }))
                                    }
                                    onMouseEnter={() => setHoveredId(item.name)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    className={rowControls[item.row] ? "" : "fadeOut"}
                                >
                                    <PaperTitle>{namesAndHelpersObject[item.name].name}</PaperTitle>
                                    <Stack
                                        direction="column"
                                        justifyContent="space-between"
                                        style={{ height: "79%" }}
                                    >
                                        <PaperHelper>
                                            {namesAndHelpersObject[item.name].helper}
                                        </PaperHelper>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            style={{ width: "100%", paddingRight: "16px" }}
                                        >
                                            <AnalysisSlider
                                                defaultValue={item.mean}
                                                mean={item.mean}
                                                std={item.std}
                                                max={Math.min(item.mean + 2 * item.std, item.upper)}
                                                min={Math.max(item.mean - 2 * item.std, item.lower)}
                                                units={item.units}
                                                setIsDirty={setIsDirty}
                                                interventionData={interventionData}
                                                setInterventionData={setInterventionData}
                                                id={item.name}
                                            />
                                            <Stack direction="row">
                                                <Text>
                                                    {causalValueFormatter({
                                                        units: item.units,
                                                        value: item.mean,
                                                    })}
                                                </Text>
                                                {isCalculated &&
                                                    interventionResponse &&
                                                    interventionResponse.intervention && (
                                                        <CalculatedText
                                                            initial={
                                                                interventionResponse.baseline[
                                                                    item.name
                                                                ]
                                                            }
                                                            calculated={
                                                                interventionResponse.intervention[
                                                                    item.name
                                                                ]
                                                            }
                                                        >
                                                            {`(${causalValueFormatter({
                                                                units: item.units,
                                                                value: interventionResponse
                                                                    .intervention[item.name],
                                                            })})`}
                                                        </CalculatedText>
                                                    )}
                                                <Text style={{ fontWeight: "200" }}>
                                                    {item.type}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                    {item.edges.map((edge) => (
                                        <Xarrow
                                            zIndex={-100}
                                            key={`${edge.source}-${edge.target}`}
                                            start={edge.source}
                                            end={edge.target}
                                            startAnchor="top"
                                            endAnchor="bottom"
                                            headSize={0}
                                            lineColor={
                                                hoveredId === null || hoveredId === item.name
                                                    ? CalculateLineColor({
                                                          weight: edge.weight,
                                                          theme,
                                                      })
                                                    : "#3d3d3d"
                                            }
                                            strokeWidth={calculateStrokeWidth(edge.weight)}
                                            animateDrawing
                                            curveness={0.3}
                                            labels={
                                                hoveredId === item.name ? (
                                                    <CustomLabel text={`${edge.weight}`} />
                                                ) : null
                                            }
                                            dashness={{
                                                strokeLen: 10,
                                                nonStrokeLen: 10,
                                                animation: -0.5,
                                            }}
                                        />
                                    ))}
                                </CustomPaper>
                            ))}
                </Stack>
            </Xwrapper>
        </Stack>
    );
};

export default AnalysisTreeProduction;
